import { HttpClient, HttpClientRequest, HttpClientResponse } from "@effect/platform";
import { Effect, Schema } from "effect";
import { runtime } from "./runtime.ts";

// `GET /api/launches/access`'s own client — a plain `get`, not `post`'s shape, so it stays separate
// rather than forcing `post`'s error-body-on-4xx contract onto a GET. This is the web UI's own
// visibility gate for the studio-admin affordances; the run-mutating endpoints still enforce the
// same server-side regardless (`rest.ts`'s `requireLaunchAccess` / `LaunchAccessDeniedHttp`).
// Mirrors `Viewer.role` server-side. Declared here rather than imported from `@curriculum-studio/core`
// for the same reason every other wire type in this file is: this is the WIRE shape, and the web app
// decodes what the endpoint sends rather than sharing the server's domain schema.
export const ViewerRoleWire = Schema.Literal("studio-pusher", "studio-admin", "studio-viewer");
export type ViewerRoleWire = typeof ViewerRoleWire.Type;

// Mirrors `mayLaunch` server-side. `undefined` (role not fetched yet) hides the affordance.
export const mayLaunch = (role: ViewerRoleWire | undefined): boolean =>
  role === "studio-admin" || role === "studio-pusher";

// Mirrors `isStudioAdmin` server-side, for the few actions a launcher may not take.
export const isStudioAdmin = (role: ViewerRoleWire | undefined): boolean => role === "studio-admin";

export const fetchLaunchAccess = (): Promise<ViewerRoleWire> =>
  runtime.runPromise(
    Effect.gen(function* () {
      const client = yield* HttpClient.HttpClient;
      const response = yield* client.execute(HttpClientRequest.get("/api/launches/access"));
      return yield* HttpClientResponse.schemaBodyJson(Schema.Struct({ role: ViewerRoleWire }))(
        response,
      );
    }).pipe(
      Effect.scoped,
      Effect.map((body) => body.role),
      // Fail closed: a network hiccup or an unexpected response resolves to the LOWER role, hiding
      // the button rather than showing it — the server-side guard is the actual enforcement point,
      // but this client-side check should never accidentally show an affordance it isn't sure is
      // allowed.
      Effect.catchAll(() => Effect.succeed("studio-viewer" as const)),
    ),
  );
